export const ONBOARDING_STATUSES = [
  "register_pending",
  "survey_pending",
  "profile_pending",
  "moderation_pending",
  "moderation_passed",
  "active",
] as const

export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number]

export const SHOP_MEMBER_ROLES = [
  "shop-owner",
  "shop-manager",
  "shop-product-manager",
  "shop-order-manager",
  "shop-support",
] as const

export type ShopMemberRole = (typeof SHOP_MEMBER_ROLES)[number]

/**
 * Разделы кабинета, по которым владелец расставляет доступы сотруднику
 * (матрица из макетов V1). Роль остаётся умолчанием: у сотрудника без
 * матрицы права по-прежнему выводятся из роли.
 */
export const SHOP_ACCESS_SECTIONS = [
  "products",
  "sales",
  "promotion",
  "finances",
  "analytics",
  "reports",
  "boutique",
] as const

export type ShopAccessSection = (typeof SHOP_ACCESS_SECTIONS)[number]

/** `full` включает все разделы разом — отдельная колонка «Полный доступ». */
export type ShopAccessMatrix = Partial<Record<ShopAccessSection | "full", boolean>>

export const hasSectionAccess = (
  matrix: ShopAccessMatrix | null | undefined,
  section: ShopAccessSection,
): boolean => Boolean(matrix && (matrix.full || matrix[section]))

export const USER_ROLES = [
  "buyer",
  "seller",
  "manufacturer",
  "verified_seller",
  ...SHOP_MEMBER_ROLES,
  "admin",
] as const

export type KnownUserRole = (typeof USER_ROLES)[number]
export type UserRole = KnownUserRole | (string & {})

/** Canonical catalog product lifecycle statuses. */
export const PRODUCT_STATUSES = [
  "published",
  "moderation",
  "rejected",
  "draft",
  "blocked",
  "archived",
] as const

export type ProductStatus = (typeof PRODUCT_STATUSES)[number]

/** Canonical order lifecycle statuses. `pending`/`declined` are payment-edge
 *  states; once payment is approved the fulfillment lifecycle runs
 *  `new` → … → `completed`. `paid`/`processing` remain readable for legacy
 *  snapshots written before the lifecycle model landed. */
export const ORDER_STATUSES = [
  "pending",
  "declined",
  "paid",
  "processing",
  "new",
  "confirmed",
  "in_work",
  "ready_to_ship",
  "shipped",
  "in_transit",
  /* Заказ ПРИВЕЗЛИ в пункт выдачи и он ждёт покупателя.
     Отдельный статус, потому что `ready_to_ship` — это склад ПРОДАВЦА
     («собрал, готов отдать перевозчику»), а здесь товар уже у места выдачи,
     и следующий шаг делает покупатель, а не продавец. Клиент подписывал
     складской статус «Получите», обещая забрать то, что ещё не уехало. */
  "awaiting_pickup",
  "delivered",
  "completed",
  "cancelled",
  "return_requested",
  "dispute_open",
  "archived",
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

/** Canonical inspiration-idea moderation lifecycle:
 *  draft → moderation → published | rejected → archived.
 *  `blocked` — модератор снял идею с витрины жёстко: в отличие от `rejected`,
 *  продавец не может её править и переотправлять, только удалить. */
export const IDEA_STATUSES = [
  "draft",
  "moderation",
  "published",
  "rejected",
  "blocked",
  "archived",
] as const

export type IdeaStatus = (typeof IDEA_STATUSES)[number]

/** Minimal shape accepted by the policy helpers; app-specific User types can be passed directly. */
export type AuthPolicySubject = {
  role?: UserRole | null
  onboardingStatus?: OnboardingStatus | (string & {}) | null
}

export const VERIFIED_SELLER_ROLE = "verified_seller" as const
export const ADMIN_ROLE = "admin" as const
export const SHOP_OWNER_ROLE = "shop-owner" as const
export const SHOP_MANAGER_ROLE = "shop-manager" as const
export const SHOP_PRODUCT_MANAGER_ROLE = "shop-product-manager" as const
export const SHOP_ORDER_MANAGER_ROLE = "shop-order-manager" as const
export const SHOP_SUPPORT_ROLE = "shop-support" as const

export const SELLER_DASHBOARD_ROLES = [
  "seller",
  "verified_seller",
  "shop-owner",
  "shop-manager",
  "shop-product-manager",
  "shop-order-manager",
  "shop-support",
] as const satisfies readonly KnownUserRole[]

export const STAFF_MANAGER_ROLES = [
  "verified_seller",
  "shop-owner",
] as const satisfies readonly KnownUserRole[]

export const STAFF_VIEWER_ROLES = [
  "verified_seller",
  "shop-owner",
  "shop-manager",
] as const satisfies readonly KnownUserRole[]

export const CATALOG_MANAGER_ROLES = [
  "verified_seller",
  "shop-owner",
  "shop-manager",
  "shop-product-manager",
] as const satisfies readonly KnownUserRole[]

export const IDEA_MANAGER_ROLES = [
  "verified_seller",
  "shop-owner",
  "shop-manager",
  "shop-product-manager",
] as const satisfies readonly KnownUserRole[]

export const BOUTIQUE_MANAGER_ROLES = [
  "verified_seller",
  "shop-owner",
  "shop-manager",
] as const satisfies readonly KnownUserRole[]

export const ORDER_VIEWER_ROLES = [
  "verified_seller",
  "shop-owner",
  "shop-manager",
  "shop-order-manager",
] as const satisfies readonly KnownUserRole[]

const hasRole = (subject: AuthPolicySubject | null | undefined, roles: readonly string[]): boolean => {
  const role = subject?.role
  return typeof role === "string" && roles.includes(role)
}

/** Seller onboarding profile is complete and normal seller-console pages may be shown. */
export const isOnboardingActive = (subject: AuthPolicySubject | null | undefined): boolean =>
  subject?.onboardingStatus === "active"

/** Seller moderation is approved. Some apps keep approved sellers in
 *  `moderation_passed` until the profile screen marks them fully `active`. */
export const isOnboardingApproved = (subject: AuthPolicySubject | null | undefined): boolean =>
  subject?.onboardingStatus === "active" || subject?.onboardingStatus === "moderation_passed"

/** The canonical marketplace seller role granted after moderation approval. */
export const isVerifiedSeller = (subject: AuthPolicySubject | null | undefined): boolean =>
  subject?.role === VERIFIED_SELLER_ROLE

/** Any non-admin seller/shop role that belongs in the seller dashboard shell. */
export const isSellerDashboardRole = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, SELLER_DASHBOARD_ROLES)

/** Seller dashboard pages are available only after onboarding is fully active. */
export const canViewSellerDashboard = (subject: AuthPolicySubject | null | undefined): boolean =>
  isSellerDashboardRole(subject) && isOnboardingActive(subject)

/** Owner-only staff invitation/removal/role management. */
export const canManageStaff = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, STAFF_MANAGER_ROLES) && isOnboardingApproved(subject)

/** Staff list visibility for owners and shop managers. */
export const canViewStaff = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, STAFF_VIEWER_ROLES) && isOnboardingApproved(subject)

/** Catalog authoring and product management. */
export const canManageCatalog = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, CATALOG_MANAGER_ROLES) && isOnboardingApproved(subject)

/** Inspiration/idea authoring and management. */
export const canManageIdeas = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, IDEA_MANAGER_ROLES) && isOnboardingApproved(subject)

/** Boutique editor and publish controls. */
export const canManageBoutique = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, BOUTIQUE_MANAGER_ROLES) && isOnboardingApproved(subject)

/** Seller sales/order reads. */
export const canViewOrders = (subject: AuthPolicySubject | null | undefined): boolean =>
  hasRole(subject, ORDER_VIEWER_ROLES) && isOnboardingApproved(subject)

/** Admin console moderation routes. Deliberately role-only: admins do not use seller onboarding. */
export const canModerateAdmin = (subject: AuthPolicySubject | null | undefined): boolean =>
  subject?.role === ADMIN_ROLE
